import type { ApplicationSettings } from "../store/schema/ApplicationSettings";
import type { ApplicationSettingsError, ApplicationSettingsReport, EcsRolloutConfig, } from "owa-analytics-types";

function getEcsRolloutConfig(ecsConfigIds: string): EcsRolloutConfig | undefined {
    const rolloutConfigIds = new Set<string>();
    const configAliases = new Set<string>();
    const tokenByRollout = new Map<string, string>();
    
    for (const entry of ecsConfigIds.split(",")) {
        const token = entry.trim();
        const parts = token.split("-");
        if (parts.length === 5 &&
            parts[0] === "P" &&
            parts[1] === "R" &&
            /^\d+$/.test(parts[2]) &&
            /^C?\d+$/.test(parts[3]) &&
            /^\d+$/.test(parts[4])) {
            const strippedToken = parts.slice(2).join("-");
            rolloutConfigIds.add(strippedToken);
            tokenByRollout.set(parts[2], strippedToken);
        }
        else if (/^pr\d+c?\d*:\d+$/.test(token)) {
            configAliases.add(token);
        }
    }
    
    if (rolloutConfigIds.size === 0 && configAliases.size === 0) {
        return undefined;
    }
    const pairedStages = getPairedStages(tokenByRollout, configAliases);
    return {
        ...(rolloutConfigIds.size > 0 && {
            EcsRolloutIds: [...rolloutConfigIds].join(","),
        }),
        ...(configAliases.size > 0 && { EcsConfigAliases: [...configAliases].join(",") }),
        ...(pairedStages && { EcsPairedStages: pairedStages }),
    };
}

/**
 * Emits only rollout stages that are a live control/treatment pair for this session,
 * as raw ECS stage tokens matching the entries in EcsRolloutIds.
 *
 * Config aliases exist only while a stage is an in-flight AzureExp step with a live
 * control arm, so completed and unstarted stages are excluded automatically. Because
 * the alias filter has already applied that scope, a token whose stage lacks a leading
 * "C" is the treatment arm of a live pair. Arm pairing (control C<n> with treatment
 * <n+1>) is deliberately NOT applied here; it is derived downstream so the raw stage
 * number is preserved.
 */
function getPairedStages(tokenByRollout: Map<string, string>, configAliases: Set<string>): string | undefined {
    const inScope = new Set<string>();
    
    for (const alias of configAliases) {
        const colon = alias.indexOf(":");
        if (colon < 0) {
            continue;
        }
        const body = alias.slice(2, colon);
        const controlDelimiter = body.indexOf("c");
        
        if (controlDelimiter > 0) {
            inScope.add(body.slice(0, controlDelimiter));
        }
        else {
            for (let length = Math.min(body.length - 1, 9); length >= 5; length--) {
                const candidate = body.slice(0, length);
                if (tokenByRollout.has(candidate)) {
                    inScope.add(candidate);
                    break;
                }
            }
        }
    }
    
    const pairedStages: string[] = [];
    for (const rollout of inScope) {
        const token = tokenByRollout.get(rollout);
        if (token) {
            pairedStages.push(token);
        }
    }
    
    return pairedStages.length > 0 ? pairedStages.join(",") : undefined;
}

export const generateApplicationSettingsReport = (receivedSettings: ApplicationSettings, receivedSettingsType: string, filters: string, ecsConfigIds: string, ecsETag?: string | null, lastError?: ApplicationSettingsError): ApplicationSettingsReport<ApplicationSettings> => {
    const report = {} as ApplicationSettingsReport<ApplicationSettings>;
    if (receivedSettingsType) {
        report.Type = receivedSettingsType;
    }
    
    report.Settings = receivedSettings;
    report.Filters = filters;
    
    // EcsConfigIds is optional and only populated once the server exposes the raw
    // config-ID list on the boot configuration. When absent the report omits the
    // field entirely so the empty value is not promoted to a payload key, which
    // matters because 1DS/Aria does not promote a consistently-empty key into a
    // Kusto column.
    if (ecsConfigIds) {
        report.EcsConfigIds = ecsConfigIds;
        Object.assign(report, getEcsRolloutConfig(ecsConfigIds));
    }
    
    // EcsETag is optional and only populated once the server exposes it on the
    // boot configuration. When absent the report omits the field entirely so
    // downstream stamping/serialization stays a no-op.
    if (ecsETag) {
        report.EcsETag = ecsETag;
    }
    
    if (lastError) {
        report.LastError = lastError;
    }
    
    return report;
};

export interface NoSettingsReport {
    NoSettings: boolean;
    LastError: ApplicationSettingsError;
}

export const getNoSettingsReport = (settingsError: ApplicationSettingsError): NoSettingsReport => ({
    NoSettings: true,
    LastError: settingsError,
});
